import { Dispatch, FC, SetStateAction, useState } from 'react';
import { mapServices } from 'src/services';
import { setAlertResponse } from '@/store';
import { typeHTHTerritory, typeMapDoc, typeMapSelection, typeMapToast } from '@monorepo/models';
import { useDispatch } from 'react-redux';

type propsType = {
    clearSelection: () => void;
    hthTerritories: typeHTHTerritory[];
    mapDoc: typeMapDoc;
    selectedSelection: string;
    selectedTerritories: string[];
    setSelectedSelection: Dispatch<SetStateAction<string>>;
    setSelectedTerritories: Dispatch<SetStateAction<string[]>>;
    setSelectionMode: Dispatch<SetStateAction<boolean>>;
    setToast: Dispatch<SetStateAction<typeMapToast | null>>;
};

export const HTHInteractiveMapSelectionMode: FC<propsType> = ({
    clearSelection, hthTerritories, mapDoc, selectedSelection, selectedTerritories,
    setSelectedSelection, setSelectedTerritories, setSelectionMode, setToast
}) => {
    const [loadingSelection, setLoadingSelection] = useState(false);
    const [savedSelections, setSavedSelections] = useState<typeMapSelection[]>(mapDoc.selections);
    const dispatch = useDispatch();

    const totalTerritories = hthTerritories ? hthTerritories.length : 0;
    //
    const selectedCount = selectedTerritories.length;
    //
    const percentSelected = totalTerritories > 0 ? Math.round((selectedCount / totalTerritories) * 100) : 0;

    const handleLoadSelection = (name: string) => {
        const sel = savedSelections.find(s => s.name === name);
        if (sel) {
            setSelectionMode(true);
            setSelectedTerritories(sel.territories);
            setSelectedSelection(name);
            setToast({ msg: 'Selección cargada', type: 'info' });
        }
    }

    const handleDeleteSelection = async (name: string) => {
        if (!window.confirm('¿Eliminar la selección guardada?')) {
            return;
        }
        setLoadingSelection(true);
        const response = await mapServices.DeleteSelection(name);
        setLoadingSelection(false);
        if (response.success) {
            const newSelections = savedSelections.filter(s => s.name !== name);
            setSavedSelections(newSelections);
            setSelectedSelection('');
            setToast({ msg: 'Selección eliminada', type: 'danger' });
        } else {
            setToast({ msg: 'Error al eliminar selección', type: 'danger' });
            dispatch(setAlertResponse({ response }));
        }
    }

    const handleSaveSelection = async () => {
        let name = prompt('Nombre para la selección:');
        if (!name) {
            return;
        }
        name = name.trim();
        if (!name) {
            return;
        }
        const exists = savedSelections.find(sel => sel.name === name);
        if (exists) {
            if (!window.confirm('Ya existe una selección con ese nombre. ¿Deseas sobrescribirla?')) {
                return;
            }
            setLoadingSelection(true);
            const response = await mapServices.UpdateSelection({ name, territories: selectedTerritories });
            setLoadingSelection(false);
            if (response.success) {
                const newSelections = savedSelections.map(sel => sel.name === name ? { name, territories: selectedTerritories } : sel);
                setSavedSelections(newSelections);
                setToast({ msg: 'Selección sobrescrita', type: 'info' });
            } else {
                setToast({ msg: 'Error al guardar selección', type: 'danger' });
                dispatch(setAlertResponse({ response }));
            }
        } else {
            setLoadingSelection(true);
            const response = await mapServices.AddSelection({ name, territories: selectedTerritories });
            setLoadingSelection(false);
            if (response.success) {
                const newSelections = [...savedSelections, { name, territories: selectedTerritories }];
                setSavedSelections(newSelections);
                setToast({ msg: 'Selección guardada', type: 'success' });
            } else {
                setToast({ msg: 'Error al guardar selección', type: 'danger' });
                dispatch(setAlertResponse({ response }));
            }
        }
    }

    return (
        <>
        <div className={'alert alert-warning d-flex align-items-center gap-3 py-2 mb-3'}>
            <strong>Modo selección activado</strong>
        </div>
        <div className={'d-flex flex-wrap align-items-center bg-lightdark border rounded gap-2 p-2'}>
            <select className={'form-select form-select-sm w-auto'}
                value={selectedSelection}
                onChange={e => {
                    if (e.target.value === '__none__') {
                        clearSelection();
                        return;
                    }
                    handleLoadSelection(e.target.value);
                }}
            >
                <option value={'__none__'}>
                    Ninguno
                </option>
                <option value={''}>
                    Cargar selección...
                </option>
                {savedSelections.map(sel =>
                    <option key={sel.name} value={sel.name}>
                        {sel.name}
                    </option>
                )}
            </select>
            <button className={'btn btn-info btn-sm'}
                title='Guarda la selección actual de territorios'
                onClick={handleSaveSelection}
                disabled={selectedTerritories.length === 0 || loadingSelection}
            >
                {loadingSelection ?
                    <>
                    <span className={'spinner-border spinner-border-sm me-1'} role={'status'} aria-hidden={'true'}></span>
                    Guardando...
                    </>
                    :
                    'Guardar selección'
                }
            </button>
            {selectedSelection && (
                <button className={'btn btn-danger btn-sm'}
                    onClick={() => handleDeleteSelection(selectedSelection)}
                    disabled={loadingSelection}
                >
                    {loadingSelection ?
                        <>
                        <span className={'spinner-border spinner-border-sm me-1'} role={'status'} aria-hidden={'true'}></span>
                        Eliminando...
                        </>
                        :
                        'Eliminar selección'
                    }
                </button>
            )}
            <button className={'btn btn-outline-secondary btn-sm'}
                onClick={clearSelection}
                disabled={loadingSelection}
            >
                Limpiar selección
            </button>
            <span className={'badge bg-primary ms-2'} style={{ fontSize: '1em' }}>
                {percentSelected}% ({selectedCount}/{totalTerritories})
            </span>
        </div>
        </>
    );
}
